import {defineStore} from 'pinia'
import api from '@/services/api'
import {useComponentsStore} from "@/stores/components"
import {useUserStore} from "@/stores/user"
import {useModalsStore} from "@/stores/modals"
import {useItemDataStore} from "@/stores/itemData"

export const useThemesStore = defineStore('themes', {
    state: () => ({
        itemsUsagePromise: false,
        allGroupName: '- All -',
        items: [],
        codeMirror: false,
        editors: [],
        
        searchStr: '',
        
        sectionName: undefined,
        tableName: false,
        
        activeId: undefined, // Must be undefined so if referenced in routes will not add an empty param
        activeEditorKey: 0,
        
        sortAlpha: false,
        navSearch: '',
        
        findModalItemIndex: 0,
        findModalSelections: [],
        
        key: 0, // Incremental key for v-for key binding.
        
        noneGroupName: '- None -',
        activeCssGroupName: '- All -',
        cssGroupNames: [],
        
        sectionsData: [
            {
                title: 'Page templates',
                tableName: 'page_templates',
                fieldName: 'templateHtml',
                mode: 'text/html'
            },
            {
                title: 'Page sections',
                tableName: 'page_sections',
                fieldName: 'templateHtml',
                mode: 'text/html'
            },
            {
                title: 'Page containers',
                tableName: 'page_containers',
                fieldName: 'templateHtml',
                mode: 'text/html'
            },
            {
                title: 'Templates',
                tableName: 'templates_templates',
                fieldName: 'templateHtml',
                mode: 'text/html'
            },
            {
                title: 'Content types',
                tableName: 'content_types',
                fieldName: 'templateHtml',
                mode: 'text/html'
            },
            {
                title: 'Scripts',
                tableName: 'themes_javascript',
                fieldName: 'script',
                mode: 'javascript'
            },
            {
                title: 'CSS',
                tableName: 'themes_css',
                fieldName: 'css',
                mode: 'css'
            }
        ],
    }),
    
    getters: {
        getSectionsSettings: (state) => (tableName) => {
            return state.sectionsData.filter((obj) => {
                return obj.tableName === tableName
            })[0]
        },
        // Searches through the editors to find one with a matching tableName and id and removes it.
        editorTabIndex: (state) => (tableName, id) => {
            let index = -1
            state.editors.forEach((editor, key) => {
                if (
                    editor.tableName === tableName
                    && editor.id === id
                ) {
                    index = key
                }
            })
            return index
        }
    },
    
    actions: {
        setActiveId(activeId) {
            this.activeId = activeId >= 0 ? parseInt(activeId) : undefined
        },
        setActiveEditorKey(activeEditorKey) {
            this.activeEditorKey = activeEditorKey
        },
        setCodeMirror(codeMirror) {
            this.codeMirror = codeMirror
        },
        setFindModalSelections(selections) {
            this.findModalSelections = selections
        },
        toggleSortAlpha() {
            this.sortAlpha = !this.sortAlpha
        },
        setNavSearch(str) {
            this.navSearch = str
        },
        onModifiedItems({userId, tableName, id}) {
            this.updateEditors({userId, tableName, id})
            this.updateNavSections({userId, tableName})
            this.setCssGroupNames()
        },
        init() {
            // Shows an "Are you sure you wish to close this window?" message if modified editor panels exist.
            window.addEventListener('beforeunload', (e) => {
                if (this.editors.filter(editor => editor.isModified).length) {
                    e.returnValue = true
                }
            })
        },
        setSectionName(sectionName) {
            this.sectionName = sectionName
            
            this.tableName = ['page_templates', 'page_sections', 'page_containers', 'templates_templates', 'content_types'].indexOf(sectionName) > -1
                ? 'templates_templates'
                : sectionName
            
            this.setItems() // Must be called after tableName is set.
            this.setCssGroupNames()
            this.updateItemsUsage()
        },
        async setItems() {
            const itemDataStore = useItemDataStore()
            
            // This is so that no items are presented when the help box appears. Otherwise, when changing section, the
            // new section's help box appears before the old section's items are removed, which looks odd.
            this.items.length = 0
            
            const sectionName = this.sectionName
            const tableName = this.tableName
            
            let type = ''
            if (sectionName === 'page_templates') {
                type = 'page'
            } else if (sectionName === 'page_sections') {
                type = 'section'
            } else if (sectionName === 'page_containers') {
                type = 'container'
            } else if (sectionName === 'content_types') {
                type = 'contentType'
            }
            
            // Fetch component IDs
            const response = await api.get(`api/component/${tableName}/id`, {
                params: {sort: 'title', type}
            })
            
            // Preload item data using the returned IDs via itemDataStore
            let items = await itemDataStore.preload({
                tableName,
                ids: response.data.values,
            })
            
            // Filter items by CSS group if applicable
            if (
                this.activeCssGroupName
                && this.activeCssGroupName !== this.allGroupName
            ) {
                items = items.filter((obj) => {
                    if (this.activeCssGroupName === this.noneGroupName) {
                        return obj.groups.length === 0
                    }
                    
                    return obj.groups.indexOf(this.activeCssGroupName) > -1
                })
            }
            
            this.items = items
        },
        async setCssGroupNames() {
            if (this.tableName === 'themes_css') {
                const response = await api.get('api/component/themes_css', {
                    params: {
                        sort: 'title'
                    }
                })
                
                let groupNames = []
                response.data.items.forEach((item) => {
                    if (item.groups) {
                        item.groups.forEach((group) => {
                            if (groupNames.indexOf(group) === -1) {
                                groupNames.push(group)
                            }
                        })
                    }
                })
                this.cssGroupNames = groupNames.sort()
                
                // If the last instance of the group has been removed, and it was the
                // menu's active group, then unset the active group to show all again.
                if (
                    this.activeCssGroupName
                    && groupNames.indexOf(this.activeCssGroupName) === -1
                ) {
                    this.setActiveCssGroupName(this.allGroupName)
                }
            }
        },
        setActiveCssGroupName(groupName) {
            this.activeCssGroupName = groupName
            this.setItems()
        },
        async createEditor({sectionName, id}) {
            const itemDataStore = useItemDataStore()
            this.setActiveId(id)
            
            const tableName = sectionName === 'page_templates' || sectionName === 'page_sections' || sectionName === 'page_containers' || sectionName === 'content_types'
                ? 'templates_templates'
                : sectionName
            
            // Search for an existing editor for this item.
            let foundKey = false
            this.editors.forEach((obj, key) => {
                if (
                    foundKey === false
                    && obj.tableName === tableName
                    && obj.id === this.activeId
                ) {
                    foundKey = key
                }
            })
            
            if (foundKey !== false) {
                this.setActiveEditorKey(foundKey)
            } else {
                /*
                // Start user tracking the item
                const componentsStore = useComponentsStore();
                let componentId = componentsStore.componentIds[tableName];
                userTrackingStore.start({
                    componentId: componentId,
                    itemId: this.activeId,
                });
                //*/
                const obj = await itemDataStore.get({
                    tableName: tableName,
                    id: this.activeId,
                })
                
                let settings = this.getSectionsSettings(sectionName)
                
                let mode = settings.mode
                if (
                    mode === 'css'
                    && obj.mode !== ''
                ) {
                    mode = obj.mode
                }
                
                this.key++
                
                this.editors.push({
                    // Incremental key for v-for :key
                    key: this.key,
                    sectionName: sectionName,
                    tableName: tableName,
                    id: this.activeId,
                    mode: mode,
                    // Stores an instance of the Code Mirror doc element, returned by getDoc().
                    // This is integral to the ability of allowing the editor to retain its
                    // history state when flicking between tabs.
                    doc: false,
                    code: obj[settings.fieldName] || '',
                    // The code mirror's focus and blur events toggle this, which
                    // is targeted to highlight the border.
                    focused: false,
                    isModified: false,
                    
                    // Used to propagate new code if another user updates it.
                    // The modified check in Themes set this and a watch in themes-editor-code-mirror
                    // applies it.
                    latestCode: ''
                })
                
                this.setActiveEditorKey(this.editors.length - 1)
            }
        },
        getEditor({tableName, id}) {
            let editor = false
            
            this.editors.forEach((obj) => {
                if (
                    obj.tableName === tableName
                    && obj.id === id
                ) {
                    editor = obj
                }
            })
            
            return editor
        },
        removeEditorByIndex({router, key}) {
            
            /*
            let editor = this.editors[key];
            let id = editor.id;
            const componentsStore = useComponentsStore();
            let componentId = componentsStore.componentIds[editor.tableName];
            userTrackingStore.stop({
                componentId: componentId,
                itemId: id,
            });
            //*/
            
            let activeEditor = this.editors[this.activeEditorKey]
            
            this.editors.splice(key, 1)
            
            // If the editor being removed is to the left of the active editor, or is the last tab,
            // then the key must be reduced by one so the active tab remains active.
            if (
                this.activeEditorKey > key
                || key === this.editors.length
            ) {
                this.setActiveEditorKey(this.activeEditorKey - 1)
            }
            
            // If the active editor is changing load the new editor's route
            let newEditor = this.editors[this.activeEditorKey]
            if (!newEditor) {
                this.setActiveId(undefined)
                router.push({query: {section: undefined, id: undefined}})
            } else if (
                newEditor.sectionName !== activeEditor.sectionName
                || newEditor.id !== activeEditor.id
            ) {
                router.push({query: {section: newEditor.sectionName, id: newEditor.id}})
            }
        },
        getItemsUsage() {
            if (!this.itemsUsagePromise) {
                this.itemsUsagePromise = this.getContentTypesUsageCount()
            }
            
            return this.itemsUsagePromise
        },
        updateItemsUsage() {
            if (
                this.sectionName === 'content_types'
                && this.itemsUsagePromise
            ) {
                this.itemsUsagePromise = this.getContentTypesUsageCount()
            }
        },
        async getContentTypesUsageCount() {
            const obj = await api.get('api/component/content/contentType')
            let usage = {}
            obj.data.values.forEach((id) => {
                usage[id] === undefined
                    ? usage[id] = 1
                    : usage[id]++
            })
            return usage
        },
        async updateEditors({userId, tableName, id}) {
            const userStore = useUserStore()
            const itemDataStore = useItemDataStore()
            
            // If the item was edited by this user do nothing.
            if (userId === userStore.user?.id) {
                return
            }
            
            const editor = this.getEditor({tableName, id})
            if (editor) {
                let code = editor.code
                
                const user = await itemDataStore.get({
                    tableName: 'users',
                    id: userId,
                })
                console.log('user', user)
                
                const obj = await itemDataStore.get({
                    tableName: tableName,
                    id: id,
                })
                
                let latestCode
                switch (tableName) {
                    case 'templates_templates':
                        latestCode = obj.templateHtml
                        break
                    case 'themes_css':
                        latestCode = obj.css
                        break
                    case 'themes_javascript':
                        latestCode = obj.script
                        break
                }
                console.log('latestCode', latestCode)
                
                let userName = user.firstName + ' ' + user.lastName
                let msg = '"' + userName + '" has saved changes to "' + obj.title + '".' +
                    '\nDo you wish to apply the changes?' +
                    '\n' +
                    '\nAny unsaved changes will be lost.'
                
                if (
                    code !== latestCode
                    && confirm(msg)
                ) {
                    editor.code = latestCode
                    editor.originalCode = latestCode
                    editor.latestCode = latestCode
                }
            }
        },
        updateNavSections({userId, tableName}) {
            const userStore = useUserStore()
            
            if (
                userStore.user?.id !== userId
                && ['templates_templates', 'themes_css', 'themes_javascript'].indexOf(tableName) > -1
            ) {
                this.setItems()
            }
        },
        async deleteItem({router, id, onSuccess}) {
            const modalsStore = useModalsStore()
            const itemDataStore = useItemDataStore()
            
            const obj = await itemDataStore.get({
                tableName: this.tableName,
                id: id,
            })
            
            let msg = `<p>Deleting <strong>${obj.title}</strong>.</p>`
            
            if (obj.type === 'contentType') {
                msg += '<p class="alert alert-warning">' +
                    '<i class="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"></i>' +
                    'Content associated to this content type will be deleted.' +
                    '</p>'
            }
            
            modalsStore.show({
                componentName: 'ConfirmModal',
                obj: {
                    modalTitle: 'Are you sure?',
                    modalBody: msg,
                    confirmStr: obj.title,
                    onConfirm: async () => {
                        await api.delete(`api/component/${this.tableName}/${id}`)
                        
                        await this.setItems()
                        await this.setCssGroupNames()
                        
                        let i = this.editorTabIndex(this.tableName, id)
                        if (i > -1) {
                            this.removeEditorByIndex({
                                router: router,
                                key: i,
                            })
                        }
                        
                        onSuccess()
                    },
                },
            })
        }
    }
})